

import React, { useRef, useState } from "react";
import { AudioOutlined, StopOutlined } from "@ant-design/icons";
import {  Input, message } from "antd";
import InspectionBtn from "./InspectionBtn";

const SpeechEnabledInput = ({
  value,
  onChange,
  placeholder,
  size = "middle",
  className = "",
  ...props
}) => {
  const [currentValue, setCurrentValue] = useState(value || "");
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  // Simple implementation using Web Speech API directly
  React.useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
       return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      message.info('Listening... Speak now');
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      // Update with final results
      if (finalTranscript) {
        const newValue = (currentValue + ' ' + finalTranscript).trim();
        setCurrentValue(newValue);
        onChange?.({ target: { value: newValue } });
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      
      switch (event.error) {
        case 'no-speech':
          toast.error('No speech detected');
          break;
        case 'not-allowed':
          toast.error('Microphone permission denied');
          break;
        case 'network':
          toast.error('Network error');
          break;
        default:
          toast.error('Speech recognition error');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [currentValue, onChange]);

  const toggleListening = async () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      // Request microphone permission first
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        recognitionRef.current.start();
      } catch (error) {
        toast.error('Microphone access denied');
      }
    }
  };

  React.useEffect(() => {
    if (value !== currentValue && !isListening) {
      setCurrentValue(value || "");
    }
  }, [value, currentValue, isListening]);

  const micButton = (
    <InspectionBtn
      type={isListening ? "primary" : "default"}
      shape="circle"
      icon={isListening ? <StopOutlined /> : <AudioOutlined />}
      onClick={toggleListening}
      size="small"
      title={isListening ? "Stop listening" : "Start voice input"}
      style={{
        backgroundColor: isListening ? "#ff4d4f" : undefined,
        borderColor: isListening ? "#ff4d4f" : undefined,
        color: isListening ? "white" : undefined,
      }}
    />
  );

  if (props?.type === "textarea") {
    return (
      <div style={{ position: "relative", width: "100%" }}>
        <Input.TextArea
          {...props}
          value={currentValue}
          onChange={(e) => {
            setCurrentValue(e.target.value);
            onChange?.(e);
          }}
          placeholder={placeholder || "Type or speak..."}
          size={size}
          className={className}
          style={{ paddingRight: 40 }}
        />
        <InspectionBtn
          type={isListening ? "primary" : "default"}
          shape="circle"
          icon={isListening ? <StopOutlined /> : <AudioOutlined />}
          size="small"
          onClick={toggleListening}
          style={{
            position: "absolute",
            right: 8,
            bottom: 8,
            zIndex: 1,
            backgroundColor: isListening ? "#ff4d4f" : undefined,
            borderColor: isListening ? "#ff4d4f" : undefined,
            color: isListening ? "white" : undefined,
          }}
        />
      </div>
    );
  }

  return (
    <Input
      {...props}
      value={currentValue}
      onChange={(e) => {
        setCurrentValue(e.target.value);
        onChange?.(e);
      }}
      placeholder={placeholder || "Type or speak..."}
      size={size}
      className={className}
      suffix={micButton}
    />
  );
};

export default SpeechEnabledInput;